import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Skeleton,
} from "@autumn/ui";
import { createContext, useContext } from "react";
import { LabelTag } from "@/components/general/LabelTag";

/** Label of the settings group the active section belongs to, shown above its title. */
export const SettingsGroupContext = createContext<string | undefined>(
	undefined,
);

interface SettingsSectionProps {
	readonly title: string;
	/** A tag beside the title, e.g. PREVIEW. */
	readonly badge?: string;
	readonly description: string;
	readonly actions?: React.ReactNode;
	readonly children: React.ReactNode;
	readonly card?: {
		readonly title: string;
		readonly description: string;
	};
}

export const SettingsSection = ({
	title,
	badge,
	description,
	actions,
	children,
	card,
}: SettingsSectionProps) => {
	const groupLabel = useContext(SettingsGroupContext);

	return (
		<div className="flex flex-col gap-10">
			<div className="flex items-end justify-between gap-6">
				<div className="flex flex-col gap-2">
					{groupLabel && (
						<span className="font-medium text-subtle text-xs leading-4">
							{groupLabel}
						</span>
					)}
					<div className="flex items-center gap-2">
						<h2 className="font-semibold text-[23px] text-foreground leading-7 tracking-[-0.02em]">
							{title}
						</h2>
						{badge && <LabelTag label={badge} />}
					</div>
					<p className="max-w-[480px] text-sm text-tertiary-foreground leading-5">
						{description}
					</p>
				</div>
				{actions}
			</div>
			{card ? (
				<Card className="shadow-none bg-interactive-secondary">
					<CardHeader>
						<CardTitle>{card.title}</CardTitle>
						<CardDescription>{card.description}</CardDescription>
					</CardHeader>
					<CardContent>{children}</CardContent>
				</Card>
			) : (
				children
			)}
		</div>
	);
};

/** Any section's shape while its code or data loads: the header lines where they will land, then its body. */
export const SettingsSectionSkeleton = () => {
	const groupLabel = useContext(SettingsGroupContext);
	return (
		<div className="flex flex-col gap-10" aria-busy="true">
			<div className="flex flex-col gap-2">
				{groupLabel && (
					<span className="font-medium text-subtle text-xs leading-4">
						{groupLabel}
					</span>
				)}
				<Skeleton className="h-7 w-40" />
				<Skeleton className="h-5 w-80" />
			</div>
			<Skeleton className="h-64 w-full rounded-xl" />
		</div>
	);
};
